import { AlertTriangle, CheckCircle, Trash } from '@lifeomic/chromicons';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import { SavedFilter } from '../../types';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import { useActiveUser } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveUser';
import { valueOrEmpty } from '../../tableUtils';
import {
  TableConfiguration,
  TableModule,
  TableModuleActions,
} from '@lifeomic/chroma-react/components/TableModule';
import * as React from 'react';
import { Radio } from '@lifeomic/chroma-react/components/Radio';

const messages = defineMessages({
  title: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.title',
    defaultMessage: 'Manage Saved Filters',
  },
  deleteConfirmation: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.deleteConfirmation',
    defaultMessage: `Are you sure you want to delete the "{name}" saved filter from the project? This filter will no longer be available to users across the project.`,
  },
  confirmDelete: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.confirmDelete',
    defaultMessage: 'Delete',
  },
  cancel: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.cancel',
    defaultMessage: 'Cancel',
  },
  filterName: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.filterName',
    defaultMessage: 'Name',
  },
  filterDescription: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.filterDescription',
    defaultMessage: 'Description',
  },
  filterOwner: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.filterOwner',
    defaultMessage: 'Owner',
  },
  filterSequenceType: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.filterSequenceType',
    defaultMessage: 'Sequence Type',
  },
  filterVariantType: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.filterVariantType',
    defaultMessage: 'Variant Type',
  },
  somatic: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.somatic',
    defaultMessage: 'Somatic',
  },
  germline: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.germline',
    defaultMessage: 'Germline',
  },
  shortVariant: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.shortVariant',
    defaultMessage: 'Short',
  },
  structuralVariant: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.structuralVariant',
    defaultMessage: 'Structural',
  },
  copyNumberVariant: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.copyNumberVariant',
    defaultMessage: 'Copy Number',
  },
  expression: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.expression',
    defaultMessage: 'Expression',
  },
  default: {
    id: 'omicsExplorer.savedFilters.manageFiltersModal.default',
    defaultMessage: 'Default',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  confirmBox: {
    background: theme.palette.black[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto 1rem',
    padding: theme.spacing(2),
    width: '25%',
    [theme.breakpoints.down('md')]: {
      width: theme.pxToRem(300),
    },
  },
  confirmIcon: {
    marginRight: theme.spacing(2),
  },
  confirmText: {
    marginBottom: theme.spacing(2),
  },
  defaultFilterIcon: {
    color: theme.palette.primary.main,
  },
}));

interface ManageFiltersModalProps {
  currentDefaultFilter: SavedFilter;
  savedFilters?: Array<SavedFilter>;
  isOpen: boolean;
  onDismiss?: () => any;
  onDeleteFilter?: (filter: SavedFilter) => any;
  onUpdateDefaultFilter: (filter: SavedFilter) => any;
}

function generateTable({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activeUser,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  canPerform,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  currentDefaultFilter,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  permissions,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  handleDeleteClick,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onUpdateDefaultFilter,
}): Array<TableConfiguration> {
  return [
    {
      header: {
        label: formatMessage(messages.default),
      },
      cell: {
        content: (filter: SavedFilter) => {
          const isDefaultFilter = filter.id === currentDefaultFilter.id;
          const hasPermissions =
            canPerform(permissions.readData) ||
            canPerform(permissions.readMaskedData) ||
            canPerform(permissions.projectAdmin);

          return hasPermissions ? (
            <Radio
              aria-label={formatMessage(messages.default)}
              defaultChecked={isDefaultFilter}
              label=""
              name="defaultFilter"
              onChange={() => onUpdateDefaultFilter(filter)}
              value={currentDefaultFilter?.id}
            />
          ) : isDefaultFilter ? (
            <CheckCircle
              aria-label={formatMessage(messages.default)}
              className={classes.defaultFilterIcon}
            />
          ) : null;
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.filterName),
      },
      cell: {
        content: (filter: SavedFilter) => {
          return valueOrEmpty(formatMessage, filter.name);
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.filterDescription),
      },
      cell: {
        content: (filter: SavedFilter) => {
          return valueOrEmpty(formatMessage, filter.description);
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.filterSequenceType),
      },
      cell: {
        content: (filter: SavedFilter) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          if (filter.sequenceType in messages) {
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            return formatMessage(messages[filter.sequenceType]);
          }

          return formatMessage(pwtSharedMessages.emptyHash);
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.filterVariantType),
      },
      cell: {
        content: (filter: SavedFilter) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          if (filter.variantType in messages) {
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            return formatMessage(messages[filter.variantType]);
          }

          return formatMessage(pwtSharedMessages.emptyHash);
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.filterOwner),
      },
      cell: {
        content: (filter: SavedFilter) => {
          return valueOrEmpty(formatMessage, filter.owner);
        },
      },
    },
    {
      header: {
        label: '',
      },
      cell: {
        content: (filter: SavedFilter) => {
          return (
            <TableModuleActions>
              <IconButton
                aria-label={`Delete filter ${filter.name}`}
                size={0}
                icon={Trash}
                onClick={() => handleDeleteClick(filter)}
                disabled={
                  !(
                    canPerform(permissions.projectAdmin) ||
                    filter.owner === activeUser.id
                  )
                }
              />
            </TableModuleActions>
          );
        },
      },
    },
  ];
}

export const ManageFiltersModal: React.FC<ManageFiltersModalProps> = ({
  currentDefaultFilter,
  savedFilters,
  isOpen,
  onDismiss,
  onDeleteFilter,
  onUpdateDefaultFilter,
  ...rootProps
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const shouldReduceMotion = useReducedMotion();

  const activeUser = useActiveUser();
  const [canPerform, permissions] = useABAC();

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [filterToDelete, setFilterToDelete] = React.useState<SavedFilter>(null);
  const cancelButtonRef = React.useRef<HTMLButtonElement>();

  React.useEffect(() => {
    if (!filterToDelete) {
      cancelButtonRef.current?.blur();
      return;
    }

    cancelButtonRef.current?.focus();
  }, [filterToDelete]);

  const handleDeleteClick = (filter: SavedFilter) => {
    setFilterToDelete(filter);
  };

  const confirmDeleteClick = (filter: SavedFilter) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    onDeleteFilter(filter);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setFilterToDelete(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={formatMessage(messages.title)}
      onDismiss={onDismiss}
      isFullScreen
      {...rootProps}
    >
      <AnimatePresence>
        {filterToDelete && (
          <motion.div
            aria-live="assertive"
            role="alert"
            className={classes.confirmBox}
            initial={
              shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -40 }
            }
            animate={
              shouldReduceMotion
                ? { opacity: 1 }
                : {
                    opacity: 1,
                    y: 0,
                  }
            }
            exit={
              shouldReduceMotion
                ? { opacity: 0 }
                : {
                    opacity: 0,
                    y: 60,
                    transition: { duration: 0.25, ease: 'easeIn' },
                  }
            }
          >
            <Box fullWidth>
              <AlertTriangle
                className={classes.confirmIcon}
                role="img"
                height={30}
                aria-hidden
              />
              <Box direction="column" fullWidth>
                <Text className={classes.confirmText}>
                  {formatMessage(messages.deleteConfirmation, {
                    name: filterToDelete.name || '',
                  })}
                </Text>
                <Box justify="flex-end" fullWidth>
                  <Button
                    variant="text"
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    onClick={() => setFilterToDelete(null)}
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    ref={cancelButtonRef}
                  >
                    {formatMessage(messages.cancel)}
                  </Button>
                  <Button onClick={() => confirmDeleteClick(filterToDelete)}>
                    {formatMessage(messages.confirmDelete)}
                  </Button>
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <TableModule
        config={generateTable({
          activeUser,
          canPerform,
          classes,
          currentDefaultFilter,
          permissions,
          formatMessage,
          handleDeleteClick,
          onUpdateDefaultFilter,
        })}
        data={savedFilters}
      />
    </Modal>
  );
};
